import { useEffect } from 'react';
import { useAuthStore } from '../store/auth';
import { fetchMe, fetchMenus } from '../api/auth';

/**
 * RBAC 上下文引导：登录态就绪后拉一次 `/auth/me` + `/auth/menus`，把角色/权限码写进 store。
 *
 * **必须在路由守卫之内也会执行的位置调用**（`App.tsx` 的 `RequireAuth`）。
 * 历史教训：这段加载原本只写在侧栏（`RegistrySidebar`）里，而侧栏在守卫**之内**——
 * 于「靠权限码才能进、角色白名单不含该角色」的页面（如坐席进 `/inbound/settings`），
 * 首帧权限码尚未加载 → 守卫判 403 → 整棵受保护子树（含侧栏）不挂载 → 权限上下文永远加载不出来
 * → 该页面对该角色**永久 403**。判据依赖权限码，加载就必须先于判据发生。
 *
 * 去重放在**模块级**而不是每个 hook 实例里：守卫与侧栏各调用一次 hook，
 * 实例级标记挡不住两边各自发一对请求（页面上会出现两份 `/auth/me` + `/auth/menus`）。
 *
 * 结果状态一律落在 store（`rbac` / `rbacError`），因此任何调用点都能读到同一份结果，
 * 重试也只需调一次 `retryRbac()`。
 */

/** 进行中的请求：同一时刻只允许一对（成功/失败后清空，允许后续重试） */
let inflight: Promise<void> | null = null;

/** 拉取 RBAC 上下文（force=false 时已有上下文直接返回） */
export function loadRbac(force = false): Promise<void> {
  const { token, rbac, setRbac, setRbacError } = useAuthStore.getState();
  if (!token) return Promise.resolve();
  if (rbac && !force) return Promise.resolve();
  if (inflight) return inflight;
  inflight = Promise.all([fetchMe({ silent: true }), fetchMenus({ silent: true })])
    .then(([me, menus]) =>
      useAuthStore.getState().setRbac({
        roles: me.roles || [],
        permissions: me.permissions || [],
        menus: menus || [],
        orgId: me.org_id || 0,
        orgScope: me.org_scope || '',
      }),
    )
    .catch((e: { msg?: string }) => {
      // 失败不再静默：置错误态由守卫给出「加载失败 + 重试」，否则判据永远处于未就绪、页面停在转圈
      setRbacError(e?.msg || '权限信息加载失败，请稍后重试');
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** 重试引导（错误态的重试入口调用）：清错误态后强制重拉 */
export function retryRbac(): Promise<void> {
  useAuthStore.getState().setRbacError(null);
  return loadRbac(true);
}

export function useRbacBootstrap(): { loaded: boolean; error: string | null; retry: () => Promise<void> } {
  const token = useAuthStore((s) => s.token);
  const rbac = useAuthStore((s) => s.rbac);
  const error = useAuthStore((s) => s.rbacError);

  useEffect(() => {
    if (token && !rbac) void loadRbac();
  }, [token, rbac]);

  return { loaded: !!rbac, error, retry: retryRbac };
}
